import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ArrowLeft, Save } from 'lucide-react'
import {
  useLaboratorio,
  useCrearLaboratorioCompleto,
  useActualizarLaboratorioCompleto,
  usePaises,
  useTiposIdentificacionFull,
  useTiposLaboratorioFull,
  useGrupos,
} from '../hooks/useBdClientes'
import { SearchableSelect } from '../components/SearchableSelect'

const laboratorioSchema = z.object({
  razon_social: z.string().min(1, 'La razón social es obligatoria').max(300),
  codigo_iso: z.string().max(10).nullable().optional(),
  grupo_id: z.number({ required_error: 'Seleccione un grupo' }).min(1, 'Seleccione un grupo'),
  tipo_identificacion_id: z.number({ required_error: 'Seleccione tipo de identificación' }).min(1, 'Seleccione tipo de identificación'),
  identificacion: z.string().min(1, 'La identificación es obligatoria').max(50),
  tipo_id: z.number({ required_error: 'Seleccione tipo de laboratorio' }).min(1, 'Seleccione tipo de laboratorio'),
  pais_id: z.number({ required_error: 'Seleccione un país' }).min(1, 'Seleccione un país'),
  direccion: z.string().min(1, 'La dirección es obligatoria'),
  correo: z.string().min(1, 'El correo es obligatorio').email('Correo inválido'),
  correo_alternativo: z.string().email('Correo alternativo inválido').nullable().optional().or(z.literal('')),
  telefono_area_contable: z.string().min(1, 'El teléfono es obligatorio').max(50),
  comentario: z.string().nullable().optional(),
  activo: z.boolean().optional(),
})

type LaboratorioFormData = z.infer<typeof laboratorioSchema>

export function LaboratorioFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id
  const labId = Number(id)

  const { data: lab, isLoading: labLoading } = useLaboratorio(isEditing ? labId : 0)
  const crearMutation = useCrearLaboratorioCompleto()
  const actualizarMutation = useActualizarLaboratorioCompleto()

  const { data: paises } = usePaises(true)
  const { data: tiposIdentificacion } = useTiposIdentificacionFull(true)
  const { data: tiposLaboratorio } = useTiposLaboratorioFull(true)
  const { data: grupos } = useGrupos()

  useDocumentTitle(isEditing ? 'Editar Laboratorio' : 'Nuevo Laboratorio')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LaboratorioFormData>({
    resolver: zodResolver(laboratorioSchema),
    defaultValues: {
      activo: true,
      grupo_id: 0,
      tipo_identificacion_id: 0,
      tipo_id: 0,
      pais_id: 0,
    },
  })

  useEffect(() => {
    if (lab && isEditing) {
      reset({
        razon_social: lab.razon_social,
        codigo_iso: lab.codigo_iso,
        grupo_id: lab.grupo_id,
        tipo_identificacion_id: lab.tipo_identificacion_id,
        identificacion: lab.identificacion,
        tipo_id: lab.tipo_id,
        pais_id: lab.pais_id,
        direccion: lab.direccion,
        correo: lab.correo,
        correo_alternativo: lab.correo_alternativo,
        telefono_area_contable: lab.telefono_area_contable,
        comentario: lab.comentario,
        activo: lab.activo,
      })
    }
  }, [lab, isEditing, reset])

  const onSubmit = async (data: LaboratorioFormData) => {
    const payload = {
      ...data,
      codigo_iso: data.codigo_iso || null,
      correo_alternativo: data.correo_alternativo || null,
      comentario: data.comentario || null,
    }

    if (isEditing) {
      await actualizarMutation.mutateAsync({ id: labId, payload })
      navigate(`/bd-clientes/laboratorios/${labId}`)
    } else {
      const result = await crearMutation.mutateAsync(payload)
      navigate(`/bd-clientes/laboratorios/${result.id}`)
    }
  }

  if (isEditing && labLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Editar Laboratorio' : 'Nuevo Laboratorio'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Info básica */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Razón Social <span className="text-red-500">*</span></Label>
                <Input {...register('razon_social')} placeholder="Razón social del laboratorio" />
                {errors.razon_social && <p className="text-sm text-red-500">{errors.razon_social.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Código ISO</Label>
                <Input {...register('codigo_iso')} placeholder="ISO" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Identificación */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Identificación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Identificación <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={tiposIdentificacion?.map(t => ({ value: t.id.toString(), label: `${t.nombre} (${t.codigo})` })) || []}
                  value={watch('tipo_identificacion_id') > 0 ? watch('tipo_identificacion_id').toString() : ''}
                  onValueChange={(v) => setValue('tipo_identificacion_id', Number(v))}
                  placeholder="Seleccione tipo"
                  searchPlaceholder="Buscar tipo..."
                />
                {errors.tipo_identificacion_id && <p className="text-sm text-red-500">{errors.tipo_identificacion_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Identificación <span className="text-red-500">*</span></Label>
                <Input {...register('identificacion')} placeholder="Número de identificación" />
                {errors.identificacion && <p className="text-sm text-red-500">{errors.identificacion.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clasificación */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Clasificación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Laboratorio <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={tiposLaboratorio?.map(t => ({ value: t.id.toString(), label: t.nombre })) || []}
                  value={watch('tipo_id') > 0 ? watch('tipo_id').toString() : ''}
                  onValueChange={(v) => setValue('tipo_id', Number(v))}
                  placeholder="Seleccione tipo"
                  searchPlaceholder="Buscar tipo..."
                />
                {errors.tipo_id && <p className="text-sm text-red-500">{errors.tipo_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Grupo <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={grupos?.map(g => ({ value: g.id.toString(), label: g.name })) || []}
                  value={watch('grupo_id') > 0 ? watch('grupo_id').toString() : ''}
                  onValueChange={(v) => setValue('grupo_id', Number(v))}
                  placeholder="Seleccione grupo"
                  searchPlaceholder="Buscar grupo..."
                />
                {errors.grupo_id && <p className="text-sm text-red-500">{errors.grupo_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>País <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={paises?.map(p => ({ value: p.id.toString(), label: p.nombre })) || []}
                  value={watch('pais_id') > 0 ? watch('pais_id').toString() : ''}
                  onValueChange={(v) => setValue('pais_id', Number(v))}
                  placeholder="Seleccione país"
                  searchPlaceholder="Buscar país..."
                />
                {errors.pais_id && <p className="text-sm text-red-500">{errors.pais_id.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Contacto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Correo <span className="text-red-500">*</span></Label>
                <Input {...register('correo')} type="email" placeholder="correo@ejemplo.com" />
                {errors.correo && <p className="text-sm text-red-500">{errors.correo.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Correo Alternativo</Label>
                <Input {...register('correo_alternativo')} type="email" placeholder="correo alternativo (opcional)" />
                {errors.correo_alternativo && <p className="text-sm text-red-500">{errors.correo_alternativo.message}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono Área Contable <span className="text-red-500">*</span></Label>
                <Input {...register('telefono_area_contable')} placeholder="Teléfono del área contable" />
                {errors.telefono_area_contable && <p className="text-sm text-red-500">{errors.telefono_area_contable.message}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dirección */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Dirección</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Dirección <span className="text-red-500">*</span></Label>
              <Input {...register('direccion')} placeholder="Dirección completa" />
              {errors.direccion && <p className="text-sm text-red-500">{errors.direccion.message}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Comentarios */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg">Comentarios</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register('comentario')}
              placeholder="Comentarios adicionales..."
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Estado (solo editar) */}
        {isEditing && (
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="text-lg">Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Activo</Label>
                  <p className="text-sm text-muted-foreground">Define si el laboratorio está activo en el sistema</p>
                </div>
                <Switch
                  checked={watch('activo') ?? true}
                  onCheckedChange={(checked) => setValue('activo', checked)}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Acciones */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || crearMutation.isPending || actualizarMutation.isPending}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Guardar Cambios' : 'Crear Laboratorio'}
          </Button>
        </div>
      </form>
    </div>
  )
}
